import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { cn } from './lib/cn';

const input = cva(
  'w-full bg-transparent text-md text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed',
  { variants: { size: { sm: 'h-8', md: 'h-9', lg: 'h-10' } }, defaultVariants: { size: 'md' } },
);

const shell = cva(
  'flex items-center gap-2 border transition-colors duration-fast focus-within:border-primary focus-within:ring-2 focus-within:ring-ring/25',
  {
    variants: {
      variant: {
        /** Standard field. */
        default: 'rounded-lg border-input bg-card px-3',
        /** Rounded search field — the top-bar "Search (Ctrl + K)". */
        search: 'rounded-pill border-input bg-secondary px-3.5 focus-within:bg-card',
        /** Chat/meeting composer — full-width, pill, no visible focus jump. */
        composer: 'rounded-pill border-input bg-card px-3.5',
        /** No chrome at all — inline rename, post composer placeholder line. */
        bare: 'rounded-lg border-transparent bg-transparent px-0',
      },
      invalid: { true: 'border-destructive focus-within:border-destructive focus-within:ring-destructive/25', false: '' },
    },
    defaultVariants: { variant: 'default', invalid: false },
  },
);

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof input>,
    VariantProps<typeof shell> {
  label?: string;
  /** Hides the label visually but keeps it for screen readers. */
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  leadingIcon?: LucideIcon;
  /** Right-hand slot — a clear button, a "Ctrl+K" chip, a send button. */
  trailing?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label, hideLabel = false, hint, error, leadingIcon: Leading, trailing,
    variant, size, className, containerClassName, id, ...props
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className={cn('w-full', containerClassName)}>
      {label ? (
        <label
          htmlFor={fieldId}
          className={cn(
            'mb-1.5 block text-base font-medium text-secondary-foreground',
            hideLabel && 'sr-only',
          )}
        >
          {label}
        </label>
      ) : null}

      <div className={cn(shell({ variant, invalid: Boolean(error) }), className)}>
        {Leading ? <Leading size={16} className="shrink-0 text-muted-foreground" aria-hidden /> : null}
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy}
          className={cn(input({ size }))}
          {...props}
        />
        {trailing ? <span className="flex shrink-0 items-center gap-1">{trailing}</span> : null}
      </div>

      {error ? (
        <p id={`${fieldId}-error`} className="mt-1.5 text-sm font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-1.5 text-sm text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
});
